import { useState, type ReactElement } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Lock } from "lucide-react";

interface SocialNotesBannerProps {
  rdSlug: string;
  socialNotes: string | null;
}

const EMPTY_PREFIX = "No social notes yet — add them on the ";
const EMPTY_LINK_TEXT = "Functional form profile";
const EMPTY_COPY = `${EMPTY_PREFIX}${EMPTY_LINK_TEXT}.`;

export function SocialNotesBanner({
  rdSlug,
  socialNotes,
}: SocialNotesBannerProps): ReactElement {
  const [expanded, setExpanded] = useState(true);
  const collapsedPreview =
    socialNotes?.split(/\r?\n/, 1)[0]?.trim() || EMPTY_COPY;

  return (
    <section
      aria-label="Social notes"
      className="overflow-hidden rounded-xl border border-sand-200 bg-sand-50"
    >
      <div className="flex min-h-[40px] items-center gap-2 px-[14px] py-[9px]">
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse social notes" : "Expand social notes"}
          className="shrink-0 text-sand-400 transition-colors hover:text-sand-500"
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </button>
        <h2 className="shrink-0 font-sans text-[11px] font-bold uppercase tracking-[0.06em] text-sand-500">
          Social notes
        </h2>
        {!expanded && (
          <p className="min-w-0 flex-1 truncate text-[13px] text-sand-400">
            {collapsedPreview}
          </p>
        )}
        {expanded && <span className="flex-1" />}
        <Lock
          aria-label="Managed on the Functional form profile"
          className="h-3.5 w-3.5 shrink-0 text-sand-400"
        />
      </div>

      {expanded && (
        <div className="pb-[14px] pl-9 pr-[14px]">
          {socialNotes ? (
            <p className="whitespace-pre-wrap text-[13.5px] leading-[21.6px] text-sand-600">
              {socialNotes}
            </p>
          ) : (
            <p className="text-[13.5px] leading-[21.6px] text-sand-600">
              {EMPTY_PREFIX}
              <Link
                to="/rds/$rdId/forms"
                params={{ rdId: rdSlug }}
                className="font-medium text-interactive underline-offset-4 hover:underline"
              >
                {EMPTY_LINK_TEXT}
              </Link>
              .
            </p>
          )}
        </div>
      )}
    </section>
  );
}
